'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'
import { cn } from '@/utilities/cn'
import { Button } from '@/components/Button'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'
export type AnchorPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center'

export interface NotificationData {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number // Duration in ms, 0 or undefined means persistent
  actions?: Array<{
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }>
}

interface NotificationPopupProps {
  notifications: NotificationData[]
  onDismiss: (id: string) => void
  anchor?: AnchorPosition
  className?: string
}

const getIcon = (type: NotificationType) => {
  const iconProps = { className: "w-5 h-5 flex-shrink-0" }
  
  switch (type) {
    case 'success':
      return <CheckCircle {...iconProps} />
    case 'error':
      return <AlertCircle {...iconProps} />
    case 'warning':
      return <AlertTriangle {...iconProps} />
    case 'info':
    default:
      return <Info {...iconProps} />
  }
}

const getColorClasses = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return {
        container: 'bg-white border-green-200',
        icon: 'text-green-600',
        title: 'text-slate-800',
        message: 'text-slate-600'
      }
    case 'error':
      return {
        container: 'bg-white border-red-200',
        icon: 'text-red-600',
        title: 'text-slate-800',
        message: 'text-slate-600'
      }
    case 'warning':
      return {
        container: 'bg-white border-amber-200',
        icon: 'text-amber-600',
        title: 'text-slate-800',
        message: 'text-slate-600'
      }
    case 'info':
    default:
      return {
        container: 'bg-white border-primary/20',
        icon: 'text-primary',
        title: 'text-slate-800',
        message: 'text-slate-600'
      }
  }
}

const getAnchorClasses = (anchor: AnchorPosition) => {
  switch (anchor) {
    case 'top-left':
      return 'top-4 left-4'
    case 'bottom-right':
      return 'bottom-4 right-4'
    case 'bottom-left':
      return 'bottom-4 left-4'
    case 'center':
      return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
    case 'top-right':
    default:
      return 'top-4 right-4'
  }
}

function NotificationItem({ 
  notification, 
  onDismiss,
  index 
}: { 
  notification: NotificationData
  onDismiss: (id: string) => void
  index: number
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const colors = getColorClasses(notification.type)

  const handleDismiss = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => onDismiss(notification.id), 300)
  }, [notification.id, onDismiss])

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 50 + index * 100)
    
    // Auto-dismiss if duration is set
    let dismissTimer: NodeJS.Timeout | null = null
    if (notification.duration && notification.duration > 0) {
      dismissTimer = setTimeout(() => {
        handleDismiss()
      }, notification.duration)
    }

    return () => {
      clearTimeout(timer)
      if (dismissTimer) clearTimeout(dismissTimer)
    }
  }, [notification.duration, index, handleDismiss])

  return (
    <div
      className={cn(
        'mb-3 max-w-sm w-full transition-all duration-300 ease-out',
        isVisible && !isExiting ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full',
        isExiting && 'opacity-0 translate-x-full scale-95'
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={cn(
        'rounded-2xl border p-5 shadow-xl backdrop-blur-sm',
        colors.container
      )}>
        <div className="flex items-start gap-3">
          <div className={colors.icon}>
            {getIcon(notification.type)}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className={cn('font-medium text-sm', colors.title)}>
              {notification.title}
            </h4>
            
            {notification.message && (
              <p className={cn('mt-1 text-sm', colors.message)}>
                {notification.message}
              </p>
            )}
            
            {notification.actions && notification.actions.length > 0 && (
              <div className="mt-3 flex gap-2">
                {notification.actions.map((action, actionIndex) => (
                  <Button
                    key={actionIndex}
                    onClick={action.onClick}
                    variant={action.variant === 'primary' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export function NotificationPopup({ 
  notifications, 
  onDismiss, 
  anchor = 'top-right',
  className = ''
}: NotificationPopupProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || notifications.length === 0) {
    return null
  }

  return createPortal(
    <div className={cn(
      'fixed z-50 pointer-events-none',
      getAnchorClasses(anchor),
      className
    )}>
      <div className="pointer-events-auto">
        {notifications.map((notification, index) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={onDismiss}
            index={index}
          />
        ))}
      </div>
    </div>,
    document.body
  )
}